import React, { useMemo } from 'react';
import StatCard from '../common/StatCard';
import EmptyState from '../common/EmptyState';
import AttentionDrilldown from '../overview/AttentionDrilldown';
import EngagedDrilldown from '../overview/EngagedDrilldown';
import DocumentsDrilldown from '../overview/DocumentsDrilldown';
import {
  BUCKETS, triageBuckets, documentCoverage, medianAccuracy, sumBy, engagedRanking,
} from '../../lib/derive';
import { attentionView, documentsView } from '../../lib/overviewView';
import { DOCUMENT_TYPES, MISSING_FILTER } from '../../lib/documents';
import {
  windowCaption, itemCount, orDash, EM_DASH,
  shortDate, longDate, axisTickIndices,
} from '../../lib/format';

/**
 * Daily activity bars with a date axis: items completed per Europe/London
 * date (GET /api/teacher-analytics/overview's daily).
 *
 * The endpoint groups by date and therefore returns ONLY days that had
 * activity -- gaps are absent rows, not zeros. Plotting them edge to edge would
 * imply consecutive days, so when the range is short enough we fill the missing
 * days with explicit zeros and label a real calendar span. Above that we plot
 * the returned points in order and say so, rather than drawing a scale we
 * cannot honestly support.
 */
const MAX_FILLED_DAYS = 120;

const DailyBars = ({ daily }) => {
  const { series, contiguous } = useMemo(() => {
    const rows = Array.isArray(daily) ? daily : [];
    if (!rows.length) return { series: [], contiguous: false };
    const sorted = [...rows].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const first = new Date(`${String(sorted[0].date).slice(0, 10)}T00:00:00Z`);
    const last = new Date(`${String(sorted[sorted.length - 1].date).slice(0, 10)}T00:00:00Z`);
    const span = Math.round((last - first) / 86400000) + 1;

    if (!Number.isFinite(span) || span <= 0 || span > MAX_FILLED_DAYS) {
      return { series: sorted, contiguous: false };
    }
    const byDate = new Map(sorted.map((d) => [String(d.date).slice(0, 10), d]));
    const filled = [];
    for (let i = 0; i < span; i += 1) {
      const day = new Date(first.getTime() + i * 86400000).toISOString().slice(0, 10);
      filled.push(byDate.get(day) || { date: day, items: 0 });
    }
    return { series: filled, contiguous: true };
  }, [daily]);

  if (!series.length) {
    return <EmptyState title="No activity in this window" body="Bars appear once students finish some work." />;
  }

  const max = Math.max(...series.map((d) => d.items || 0), 1);
  const ticks = new Set(axisTickIndices(series.length, 6));

  return (
    <figure className="td-chart">
      <div className="td-chart__plot">
        <div className="td-chart__yaxis" aria-hidden="true">
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>
        <div
          className="td-bars"
          role="img"
          aria-label={`Daily activity from ${longDate(series[0].date)} to ${longDate(series[series.length - 1].date)}, peak ${itemCount(max)}`}
        >
          {series.map((d) => {
            const pct = Math.round(((d.items || 0) / max) * 100);
            return (
              <span
                key={d.date}
                className={`td-bars__bar${d.items ? '' : ' td-bars__bar--empty'}`}
                style={{ height: `${Math.max(pct, d.items ? 2 : 0)}%` }}
                title={`${longDate(d.date)}: ${itemCount(d.items)}`}
              />
            );
          })}
        </div>
      </div>
      <div className="td-chart__xaxis" aria-hidden="true">
        {series.map((d, i) => (
          <span key={d.date} className="td-chart__tick">
            {ticks.has(i) ? shortDate(d.date) : ''}
          </span>
        ))}
      </div>
      <figcaption className="td-chart__caption">
        {contiguous
          ? `Items completed per day · ${longDate(series[0].date)} to ${longDate(series[series.length - 1].date)}`
          : `Items completed on the ${series.length} days with any · ${longDate(series[0].date)} to ${longDate(series[series.length - 1].date)} (days without activity are not shown)`}
      </figcaption>
    </figure>
  );
};

const studentsText = (n) => `${n} ${n === 1 ? 'student' : 'students'}`;

/** One Needs attention count, opening that bucket's list. */
const TriageRow = ({ label, count, onOpen }) => (
  <button
    type="button"
    className="td-triage__row td-triage__row--button"
    onClick={onOpen}
    aria-label={`${label}, ${studentsText(count)}, show list`}
  >
    <span className="td-triage__label">{label}</span>
    <span className="td-triage__count">{count}</span>
  </button>
);

/**
 * One Document coverage row: how many have the document, opening the
 * Documents list on the students without it. Inside the clickable card,
 * so it stops the click there.
 */
const CoverageRow = ({ type, n, pct, total, onOpen }) => {
  const missing = total - n;
  return (
    <li>
      <button
        type="button"
        className="td-coverage__row td-coverage__row--button"
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        aria-label={`${type.label}, ${n} of ${total}, show ${studentsText(missing)} with no ${type.noun}`}
      >
        <span className="td-coverage__label">{type.label}</span>
        <span className="td-coverage__track">
          <span className="td-coverage__fill" style={{ width: `${Math.min(100, pct)}%` }} />
        </span>
        <span className="td-coverage__value">{`${n} of ${total}`}</span>
      </button>
    </li>
  );
};

/**
 * Overview of the selected scope, or one of its drill-downs in place of
 * the grid when `view` (lib/overviewView parseView) names one.
 *
 * `students` are the tracker dashboard rows (Needs attention, accuracy);
 * `documents` the rows of GET /api/organisations/documents, the same rows
 * the Documents drill-down lists, so Documents on file and Document
 * coverage count what that list shows; `roster` the Students list rows for
 * the same scope and period (Most engaged); `overview` the active students
 * and daily items. `studentCount` is the scope's count from the scope
 * picker, the denominator for "active". Opening a drill-down calls
 * onOpenView, switching bucket or documents segment inside one
 * onSwitchView, and its "Back to overview" onCloseView.
 */
const OverviewTab = ({
  students, documents, overview, roster, rosterLoading, rosterError, period, scope, scopeLabel, studentCount,
  view, onOpenView, onSwitchView, onCloseView, onOpenStudent,
}) => {
  const counts = useMemo(() => triageBuckets(students), [students]);
  const coverage = useMemo(() => documentCoverage(documents), [documents]);
  const median = useMemo(() => medianAccuracy(students), [students]);
  const questions = useMemo(
    () => sumBy(students, 'qg_attempted') + sumBy(students, 'pp_attempted'),
    [students],
  );

  const ranked = useMemo(() => engagedRanking(roster), [roster]);
  const engaged = ranked.slice(0, 5);

  const busiest = useMemo(() => {
    const rows = Array.isArray(overview?.daily) ? overview.daily : [];
    if (!rows.length) return null;
    return rows.reduce((best, d) => ((d.items || 0) > (best.items || 0) ? d : best), rows[0]);
  }, [overview]);

  if (view?.kind === 'attention') {
    return (
      <AttentionDrilldown
        students={students}
        bucket={view.bucket}
        scope={scope}
        scopeLabel={scopeLabel}
        period={period}
        roster={roster}
        onSwitch={(bucket) => onSwitchView(attentionView(bucket))}
        onBack={onCloseView}
        onOpenStudent={onOpenStudent}
      />
    );
  }
  if (view?.kind === 'engaged') {
    return (
      <EngagedDrilldown
        roster={roster}
        loading={rosterLoading}
        error={rosterError}
        scopeLabel={scopeLabel}
        period={period}
        onBack={onCloseView}
        onOpenStudent={onOpenStudent}
      />
    );
  }
  if (view?.kind === 'documents') {
    return (
      <DocumentsDrilldown
        scope={scope}
        scopeLabel={scopeLabel}
        filter={view.filter}
        onFilterChange={(filter) => onSwitchView(documentsView(filter))}
        onBack={onCloseView}
        onOpenStudent={onOpenStudent}
      />
    );
  }

  const active = overview?.active_students;
  const caption = windowCaption(period);
  const documentsValue = `${coverage.totalDocuments} of ${coverage.capacity}`;

  return (
    <div className="td-overview">
      <div className="td-statgrid">
        <StatCard
          eyebrow="Active students"
          value={active == null ? EM_DASH : `${active} of ${studentCount}`}
          caption={caption || undefined}
        />
        <StatCard
          eyebrow="Questions attempted"
          value={questions.toLocaleString()}
          caption={caption || undefined}
        />
        <StatCard
          eyebrow="Median quiz accuracy"
          value={median == null ? EM_DASH : `${median}%`}
          caption={median == null ? 'Nobody has attempted a question yet' : 'Across students who have attempted'}
        />
        <StatCard
          eyebrow="Documents on file"
          value={documentsValue}
          caption="CV, cover letter and personal statement"
          onClick={() => onOpenView(documentsView())}
          actionLabel={`Documents on file, ${documentsValue}, show documents`}
        />
      </div>

      <div className="td-overview__split">
        <section className="td-card">
          <div className="td-card__head">
            <h3 className="td-card__title">Daily activity</h3>
            {busiest ? (
              <span className="td-card__meta">
                {`Busiest day ${busiest.date} (${itemCount(busiest.items)})`}
              </span>
            ) : null}
          </div>
          <DailyBars daily={overview?.daily} />
        </section>

        <section className="td-card">
          <h3 className="td-card__title">Needs attention</h3>
          <div className="td-triage">
            {BUCKETS.map((b) => (
              <TriageRow
                key={b.key}
                label={b.label}
                count={counts[b.key]}
                onOpen={() => onOpenView(attentionView(b.key))}
              />
            ))}
          </div>
        </section>
      </div>

      <div className="td-overview__split">
        {/* As Most engaged: the whole card opens the Documents list and each
            row opens it on the students without that document. The head's
            button is the keyboard and screen reader way in to the whole
            list, so no control is nested in another. */}
        <section className="td-card td-card--clickable" onClick={() => onOpenView(documentsView())}>
          <div className="td-card__head">
            <h3 className="td-card__title">Document coverage</h3>
            <button
              type="button"
              className="td-btn td-btn--small"
              onClick={(e) => { e.stopPropagation(); onOpenView(documentsView()); }}
              aria-label="Document coverage, show documents"
            >
              Show all
            </button>
          </div>
          {coverage.studentCount === 0 ? (
            <EmptyState title="No students yet" />
          ) : (
            <ul className="td-coverage">
              {DOCUMENT_TYPES.map((type) => (
                <CoverageRow
                  key={type.key}
                  type={type}
                  n={coverage.byType[type.key].n}
                  pct={coverage.byType[type.key].pct}
                  total={coverage.studentCount}
                  onOpen={() => onOpenView(documentsView(MISSING_FILTER[type.key]))}
                />
              ))}
            </ul>
          )}
        </section>

        {/* The whole card opens the full list; the names inside open a
            profile instead. The head's button is the keyboard and screen
            reader way in, so no control is nested in another. */}
        <section className="td-card td-card--clickable" onClick={() => onOpenView('engaged')}>
          <div className="td-card__head">
            <h3 className="td-card__title">Most engaged</h3>
            <button
              type="button"
              className="td-btn td-btn--small"
              onClick={(e) => { e.stopPropagation(); onOpenView('engaged'); }}
              aria-label={`Most engaged, ${studentsText(ranked.length)}, show list`}
            >
              Show all
            </button>
          </div>
          {rosterLoading ? <p className="td-loading">Loading…</p> : null}
          {!rosterLoading && engaged.length === 0 ? <EmptyState title="No activity yet" /> : null}
          {!rosterLoading && engaged.length > 0 ? (
            <ol className="td-ranklist">
              {engaged.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="td-ranklist__row"
                    onClick={(e) => { e.stopPropagation(); onOpenStudent(s.id); }}
                  >
                    <span className="td-ranklist__rank">{s.rank}</span>
                    <span className="td-ranklist__name">{orDash(s.name)}</span>
                    <span className="td-ranklist__meta">{itemCount(s.items, 'Session')}</span>
                  </button>
                </li>
              ))}
            </ol>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default OverviewTab;
